import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete
} from '@mui/material';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import theme from 'src/theme';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  getAddressByZipCode,
  LOAD_ADDRESS_BY_ZIP_CODE
} from 'src/store/actions/clientActions';
import useIsLargeScreen from 'src/hooks/useIsLargeScreen';

import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepThree = ({ data, handleNext, handleBack }) => {
  const { classes } = useStyles();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();

  const [addressData, setAddressData] = useState({
    cities: [],
    colonias: [],
    states: [],
    isValid: null
  });
  const isLoadingAddress = useSelector(
    isLoadingSelector([LOAD_ADDRESS_BY_ZIP_CODE.action])
  );
  const handleZipCodeChange = useCallback((e, setFieldValue) => {
    const { value } = e.target;
    setFieldValue('zipCode', value);
    if (value.length < 5) {
      setAddressData({
        cities: [],
        colonias: [],
        states: [],
        isValid: null
      });
    }
    if (value.length === 5) {
      dispatch(getAddressByZipCode(value, setAddressData));
    }
    setFieldValue('colonia', '');
    setFieldValue('city', '');
    setFieldValue('state', '');
    setFieldValue('municipality', '');
  });

  const onNext = useCallback(
    async values => {
      handleNext({ ...values });
    },
    [handleNext, data]
  );

  const canSubmit = addressData.isValid === true;

  return (
    <Box
      sx={{
        height: {
          xs: 'auto',
          md: '50vh'
        },
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          height: '20%'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            marginBottom: '10px',
            color: theme.palette.primary.main
          }}
        >
          {t('BUREAU_FORM.STEP_FIVE.TITLE')}
        </Typography>
      </Box>

      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          handleChange,
          handleBlur,
          setFieldValue
        }) => {
          const showCopomexError = !canSubmit && values.zipCode?.length === 5;
          return (
            <Box
              onSubmit={handleSubmit}
              component="form"
              sx={{
                height: '80%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box className={classes.container}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(touched.street && errors.street)}
                    sx={{
                      width: ' 48%'
                    }}
                    helperText={touched.street && t(errors.street)}
                    label={t('COMMON.STREET')}
                    margin="normal"
                    name="street"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.street}
                    variant="outlined"
                  />

                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(
                      touched.externalHomeNumber && errors.externalHomeNumber
                    )}
                    sx={{
                      width: '24%'
                    }}
                    helperText={
                      touched.externalHomeNumber && t(errors.externalHomeNumber)
                    }
                    label={
                      isLargeScreen ? t('COMMON.EXTERNAL_HOME_NUMBER') : 'Ext.'
                    }
                    margin="normal"
                    name="externalHomeNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.externalHomeNumber}
                    variant="outlined"
                  />

                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(
                      touched.insideHomeNumber && errors.insideHomeNumber
                    )}
                    sx={{
                      width: '24%'
                    }}
                    helperText={
                      touched.insideHomeNumber && t(errors.insideHomeNumber)
                    }
                    label={
                      isLargeScreen ? t('COMMON.INSIDE_HOME_NUMBER') : 'Int.'
                    }
                    margin="normal"
                    name="insideHomeNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.insideHomeNumber}
                    variant="outlined"
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={
                      Boolean(touched.zipCode && errors.zipCode) ||
                      showCopomexError
                    }
                    sx={{
                      width: '48%'
                    }}
                    helperText={
                      showCopomexError
                        ? t('ERRORS.ZIP_CODE_WAS_NOT_FOUND')
                        : touched.zipCode && t(errors.zipCode)
                    }
                    label={t('COMMON.ZIP_CODE')}
                    margin="normal"
                    name="zipCode"
                    onBlur={handleBlur}
                    onChange={e => {
                      handleZipCodeChange(e, setFieldValue);
                    }}
                    type="text"
                    value={values.zipCode}
                    variant="outlined"
                  />

                  <Autocomplete
                    freeSolo
                    options={addressData.cities}
                    value={values.city || null}
                    onChange={(event, newValue) => {
                      setFieldValue('city', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('city', newInputValue);
                    }}
                    disabled={!values.zipCode || isLoadingAddress}
                    sx={{
                      width: '50%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t('COMMON.CITY')}`
                            : t('COMMON.CITY')
                        }
                        error={Boolean(touched.city && errors.city)}
                        helperText={touched.city && t(errors.city)}
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <Autocomplete
                    freeSolo
                    options={addressData.colonias}
                    value={values.colonia || null}
                    onChange={(event, newValue) => {
                      setFieldValue('colonia', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('colonia', newInputValue);
                    }}
                    disabled={!values.zipCode || isLoadingAddress}
                    sx={{
                      width: '48%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t('COMMON.COLONIA')}`
                            : t('COMMON.COLONIA')
                        }
                        error={Boolean(touched.colonia && errors.colonia)}
                        helperText={touched.colonia && t(errors.colonia)}
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    freeSolo
                    options={addressData.states}
                    value={values.state || null}
                    onChange={(event, newValue) => {
                      setFieldValue('state', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('state', newInputValue);
                    }}
                    disabled={!values.zipCode || isLoadingAddress}
                    sx={{
                      width: '50%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t('COMMON.STATE')}`
                            : t('COMMON.STATE')
                        }
                        error={Boolean(touched.state && errors.state)}
                        helperText={touched.state && t(errors.state)}
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <Autocomplete
                    freeSolo
                    options={addressData.municipalities || []}
                    value={values.municipality || null}
                    onChange={(event, newValue) => {
                      setFieldValue('municipality', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('municipality', newInputValue);
                    }}
                    disabled={!values.zipCode || isLoadingAddress}
                    sx={{
                      width: '48%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t(
                                'COMMON.MUNICIPALITY'
                              )}`
                            : t('COMMON.MUNICIPALITY')
                        }
                        error={Boolean(
                          touched.municipality && errors.municipality
                        )}
                        helperText={
                          touched.municipality && t(errors.municipality)
                        }
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box
                className={classes.box}
                display="flex"
                justifyContent="space-between"
              >
                <Button
                  className={classes.buttons}
                  onClick={() => handleBack()}
                  color="primary"
                  variant="outlined"
                >
                  {t('COMMON.BACK')}
                </Button>
                <Button
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  color="primary"
                  variant="outlined"
                  disabled={!canSubmit}
                >
                  {t('COMMON.NEXT')}
                </Button>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};
StepThree.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func
};

export default StepThree;
